import { useSubjectMember } from '@healthblocks-io/core'
import { useCallback, useEffect } from 'react'

import { mixpanel } from '@/mixpanel'
import { useLocation, useNavigate } from '@/router-wrapper'
import { getUniversalLinkConfig } from '../helpers/universal-link'
import { useLockoutTime } from '../hooks/useLockoutTime'
import { usePendingLink } from './usePendingLink'
import { useUniversalLinkHandler } from './useUniversalLink'

const priorityFlows = new Set(['/onboarding'])

export const isHandlingUniversalLink = {
  current: false,
}

export function UniversalLinkManager() {
  const location = useLocation()
  const member = useSubjectMember()
  const { lockout } = useLockoutTime()
  const navigate = useNavigate()
  const { pendingLink, setPendingLink, resetPendingLink } = usePendingLink()

  const isLoggedIn = !!member && !lockout
  const isPriorityFlow = priorityFlows.has(location.pathname)
  const canHandleNow = isLoggedIn && !isPriorityFlow

  const handleLink = useUniversalLinkHandler()

  const handleAndClear = useCallback(
    (url: string) => {
      try {
        isHandlingUniversalLink.current = true
        handleLink(url)
      } catch (error) {
        console.error('[UniversalLinkManager] Failed to handle URL:', url, error)
      } finally {
        resetPendingLink()
        setTimeout(() => {
          isHandlingUniversalLink.current = false
        }, 1000)
      }
    },
    [handleLink, resetPendingLink],
  )

  const receiveLink = useCallback(
    (url: string) => {
      if (!url.includes('/link/')) {
        return
      }

      const linkConfig = getUniversalLinkConfig(url)

      mixpanel.track('deep_link_received', {
        url,
        action: linkConfig?.action,
      })

      // If the link is public, we can handle it immediately
      if (linkConfig && !linkConfig.requiresAuth) {
        handleAndClear(url)
        return
      }

      // If the link is private, we need to check if the user is logged in
      if (canHandleNow) {
        handleAndClear(url)
      } else {
        setPendingLink(url)

        // Since desktop handles routes differently, it will actually stay stuck on an empty page if we don't redirect when needed
        if (!isLoggedIn) {
          navigate('/login')
        }
      }
    },
    [canHandleNow, handleAndClear, setPendingLink],
  )

  // Web - On load
  useEffect(() => {
    const url = window.location.href
    if (url.includes('/link/')) {
      receiveLink(url)
    }
  }, [receiveLink, location.pathname])

  // After login or leaving a priority flow
  useEffect(() => {
    if (canHandleNow && pendingLink) {
      handleAndClear(pendingLink)
    }
  }, [canHandleNow, location.pathname, handleAndClear, pendingLink])

  return null
}
